"use client";

import { useEffect, useRef } from "react";

const STREAMING_TEXT_WIPE_MS = 280;
const HIGH_THROUGHPUT_TEXT_WIPE_MS = 90;
// A chunk that lands all at once cascades block by block (paragraph, list
// item, heading) instead of wiping every run in the same instant. Every run
// inside one block shares the block's slot, so a sentence never fills in
// from the middle. Token-sized deltas stagger by ~nothing, a whole answer
// spreads over at most this window; text is in the DOM the moment it
// arrives either way, only the reveal is staggered.
const STREAMING_TEXT_CASCADE_MAX_MS = 600;
const STREAMING_TEXT_CASCADE_MS_PER_CHAR = 0.5;
const STREAMING_TEXT_CASCADE_STEP_MS = 40;
const STREAMING_TEXT_BLOCK_TAGS = new Set([
  "p",
  "li",
  "blockquote",
  "td",
  "th",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
]);
const STREAMING_TEXT_BLOCKED_TAGS = new Set([
  "code",
  "pre",
  "kbd",
  "math",
  "script",
  "style",
]);
const STREAMING_TEXT_ELIGIBLE_TAGS = new Set([
  "p",
  "li",
  "blockquote",
  "td",
  "th",
  "strong",
  "em",
  "del",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
]);
const STREAM_STATE_LIMIT = 64;

type StreamingTextRegistryEntry = {
  startedAt: number;
  value: string;
};

export type StreamingTextState = {
  // Length of normalized source already revealed; offsets below it never
  // re-animate, even when a completed markdown construct reflows the tree.
  highWater: number;
  // Source offsets currently mid-wipe, so a re-render resumes the animation
  // via negative delay instead of restarting it.
  registry: Map<number, StreamingTextRegistryEntry>;
};

export function createStreamingTextState(): StreamingTextState {
  return { highWater: 0, registry: new Map() };
}

// Keyed by streamIdentity so a remount mid-stream resumes where the previous
// instance left off instead of replaying the wipe over the whole answer.
const streamStates = new Map<string, StreamingTextState>();

function commitStreamState(identity: string, state: StreamingTextState) {
  streamStates.delete(identity);
  while (streamStates.size >= STREAM_STATE_LIMIT) {
    const oldest = streamStates.keys().next().value;
    if (oldest === undefined) break;
    streamStates.delete(oldest);
  }
  streamStates.set(identity, state);
}

export function useStreamingMarkdownRehypePlugins({
  enabled,
  source,
  sourceOffset = 0,
  streamIdentity,
  pace,
}: {
  enabled: boolean;
  /** The whole normalized answer; reveal state is keyed by offsets into it. */
  source: string;
  /**
   * Where the markdown handed to this renderer starts inside `source`, so a
   * tail block rendered on its own keeps the same offsets and never
   * re-animates text already revealed.
   */
  sourceOffset?: number;
  streamIdentity?: string;
  pace?: "default" | "high-throughput";
}) {
  const identity = streamIdentity ?? "anonymous-stream";
  const now =
    typeof performance === "undefined" ? Date.now() : performance.now();
  const commitRef = useRef<{
    identity: string;
    state: StreamingTextState;
  } | null>(null);
  const plugins = enabled
    ? [
        () => (tree: unknown) => {
          const baseline =
            streamStates.get(identity) ?? createStreamingTextState();
          const state = applyStreamingTextAnimation(tree, {
            now,
            source,
            sourceOffset,
            state: baseline,
            durationMs:
              pace === "high-throughput"
                ? HIGH_THROUGHPUT_TEXT_WIPE_MS
                : STREAMING_TEXT_WIPE_MS,
          });
          commitRef.current = { identity, state };
        },
      ]
    : undefined;
  useEffect(() => {
    if (!enabled) {
      streamStates.delete(identity);
      commitRef.current = null;
      return;
    }
    const commit = commitRef.current;
    if (commit && commit.identity === identity) {
      commitStreamState(identity, commit.state);
    }
  });
  return plugins;
}

function isElementNode(node: unknown): node is {
  children?: unknown[];
  tagName?: string;
  type: "element";
  position?: { start?: { offset?: number } };
} {
  return Boolean(
    node &&
    typeof node === "object" &&
    (node as { type?: unknown }).type === "element",
  );
}

function isParentNode(node: unknown): node is {
  children: unknown[];
  tagName?: string;
} {
  return Boolean(
    node &&
    typeof node === "object" &&
    Array.isArray((node as { children?: unknown }).children),
  );
}

type StreamingTextNode = {
  type: "text";
  value: string;
  position?: { start?: { offset?: number }; end?: { offset?: number } };
};

function isTextNode(node: unknown): node is StreamingTextNode {
  return Boolean(
    node &&
    typeof node === "object" &&
    (node as { type?: unknown }).type === "text" &&
    typeof (node as { value?: unknown }).value === "string",
  );
}

function walkStreamingTextNodes(
  node: unknown,
  parentTag: string | null,
  blocked: boolean,
  blockStart: number | null,
  visitor: (node: StreamingTextNode, blockStart: number | null) => void,
) {
  if (isTextNode(node)) {
    if (!blocked && parentTag && STREAMING_TEXT_ELIGIBLE_TAGS.has(parentTag)) {
      visitor(node, blockStart);
    }
    return;
  }
  if (!isParentNode(node)) return;

  const tagName = isElementNode(node) ? (node.tagName ?? null) : null;
  const nextBlocked =
    blocked || (tagName ? STREAMING_TEXT_BLOCKED_TAGS.has(tagName) : false);
  // The nearest block ancestor anchors the cascade slot for everything in it.
  const ownStart =
    isElementNode(node) && tagName && STREAMING_TEXT_BLOCK_TAGS.has(tagName)
      ? node.position?.start?.offset
      : undefined;
  const nextBlockStart = typeof ownStart === "number" ? ownStart : blockStart;
  for (const child of node.children) {
    walkStreamingTextNodes(child, tagName, nextBlocked, nextBlockStart, visitor);
  }
}

function nodeSourceRange(node: StreamingTextNode, sourceOffset: number) {
  const start = node.position?.start?.offset;
  const end = node.position?.end?.offset;
  if (typeof start !== "number" || typeof end !== "number") return null;
  return { start: start + sourceOffset, end: end + sourceOffset };
}

type StreamingTextRun = {
  text: string;
  startedAt: number | null;
};

type StreamingRunInput = {
  now: number;
  highWater: number;
  sourceOffset: number;
  /** Characters newly revealed this render: [highWater, source.length). */
  newSpanLength: number;
};

function cascadeDelayMs(
  offset: number,
  input: { highWater: number; newSpanLength: number },
) {
  const chars = offset - input.highWater;
  if (chars <= 0 || input.newSpanLength <= 0) return 0;
  const raw = Math.min(
    chars * STREAMING_TEXT_CASCADE_MS_PER_CHAR,
    (chars / input.newSpanLength) * STREAMING_TEXT_CASCADE_MAX_MS,
  );
  return (
    Math.floor(raw / STREAMING_TEXT_CASCADE_STEP_MS) *
    STREAMING_TEXT_CASCADE_STEP_MS
  );
}

function registerNewChar(
  offset: number,
  anchor: number,
  source: string,
  input: StreamingRunInput,
  registry: Map<number, StreamingTextRegistryEntry>,
) {
  const startedAt = input.now + cascadeDelayMs(anchor, input);
  registry.set(offset, { startedAt, value: source[offset] ?? "" });
  return startedAt;
}

function charStartedAt(
  offset: number,
  anchor: number,
  source: string,
  input: StreamingRunInput,
  registry: Map<number, StreamingTextRegistryEntry>,
) {
  const entry = registry.get(offset);
  if (entry) return entry.startedAt;
  if (offset < input.highWater) return null;
  return registerNewChar(offset, anchor, source, input, registry);
}

function collectNodeRuns(
  node: StreamingTextNode,
  blockStart: number | null,
  source: string,
  input: StreamingRunInput,
  registry: Map<number, StreamingTextRegistryEntry>,
): StreamingTextRun[] {
  const range = nodeSourceRange(node, input.sourceOffset);
  // Without a faithful source mapping the text is treated as already shown:
  // never animating is safe, re-animating shown text is not.
  if (!range) return [{ text: node.value, startedAt: null }];
  // A block that began before this chunk keeps the front of the cascade so
  // its tail continues rather than waiting behind newer blocks.
  const anchor = Math.max(
    input.highWater,
    blockStart === null ? range.start : blockStart + input.sourceOffset,
  );

  const exact =
    range.end - range.start === node.value.length &&
    source.startsWith(node.value, range.start);
  if (!exact) {
    const startedAt =
      registry.get(range.start)?.startedAt ??
      (range.start >= input.highWater
        ? registerNewChar(range.start, anchor, source, input, registry)
        : null);
    return [{ text: node.value, startedAt }];
  }

  const runs: StreamingTextRun[] = [];
  for (let index = 0; index < node.value.length; index += 1) {
    const startedAt = charStartedAt(
      range.start + index,
      anchor,
      source,
      input,
      registry,
    );
    const current = runs.at(-1);
    if (current && current.startedAt === startedAt) {
      current.text += node.value[index] ?? "";
    } else {
      runs.push({ text: node.value[index] ?? "", startedAt });
    }
  }
  return runs;
}

export function applyStreamingTextAnimation(
  tree: unknown,
  input: {
    now: number;
    source: string;
    sourceOffset?: number;
    state: StreamingTextState;
    durationMs?: number;
  },
): StreamingTextState {
  const { now, source, state } = input;
  const sourceOffset = input.sourceOffset ?? 0;
  const durationMs = input.durationMs ?? STREAMING_TEXT_WIPE_MS;
  const registry = new Map(state.registry);
  for (const [offset, entry] of registry) {
    if (
      now - entry.startedAt >= durationMs ||
      offset >= source.length ||
      source[offset] !== entry.value
    ) {
      registry.delete(offset);
    }
  }

  const runInput: StreamingRunInput = {
    now,
    highWater: state.highWater,
    sourceOffset,
    newSpanLength: source.length - state.highWater,
  };
  walkStreamingTextNodes(tree, null, false, null, (node, blockStart) => {
    const runs = collectNodeRuns(node, blockStart, source, runInput, registry);
    if (!runs.some((run) => run.startedAt !== null)) return;

    const children = runs.map((run) => {
      if (run.startedAt === null) {
        return { type: "text", value: run.text };
      }
      // Negative: resume a wipe already underway. Positive: this run's turn
      // in the cascade has not come yet, so `both` fill keeps it clipped.
      const delayMs = Math.round(run.startedAt - now);
      return {
        type: "element",
        tagName: "span",
        properties: {
          className: ["mlx-stream-text-run"],
          style: [
            `--mlx-stream-delay: ${delayMs}ms`,
            `--mlx-stream-duration: ${durationMs}ms`,
          ].join(";"),
        },
        children: [{ type: "text", value: run.text }],
      };
    });
    Object.assign(node, {
      type: "element",
      tagName: "span",
      properties: {},
      children,
    });
  });

  return {
    highWater: Math.max(state.highWater, source.length),
    registry,
  };
}
